import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, List, Clock, Plus } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { CalendarView } from '@/components/calendar/CalendarView';
import { BookingManager } from '@/components/booking/BookingManager';
import { TodaysFocus } from '@/components/calendar/TodaysFocus';
import { ManualBooking } from '@/components/calendar/ManualBooking';
import type { Booking } from '@/types';

export function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'calendar' | 'bookings' | 'today' | 'manual'>('calendar');
  const [refreshKey, setRefreshKey] = useState(0);

  const isProvider = user?.role === 'provider';

  if (!isProvider) {
    navigate('/dashboard');
    return null;
  }

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleBookingClick = (booking: Booking) => {
    console.log('Booking clicked:', booking);
  };

  const handleTimeSlotClick = (date: string, startTime: string, endTime: string) => {
    console.log('Time slot clicked:', { date, startTime, endTime });
    // Navigate to create booking or open modal
  };

  const handleMessageCustomer = (customerId: string, bookingId: string) => {
    navigate(`/chat?customer=${customerId}&booking=${bookingId}`);
  };

  const handleBookingCreated = () => {
    // Refresh bookings and switch to bookings tab
    setRefreshKey(prev => prev + 1);
    setActiveTab('bookings');
  };

  const tabs = [
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'bookings', label: 'Bookings List', icon: List },
    { id: 'today', label: "Today's Focus", icon: Clock },
    { id: 'manual', label: 'Manual Booking', icon: Plus }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch"
      >
        Skip to main content
      </a>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Calendar' }
          ]}
        />

        <div className="mb-6 sm:mb-8 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Schedule & Bookings</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your availability and upcoming jobs.</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 sm:mb-8 bg-white rounded-lg px-2 shadow-sm overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-h-touch ${activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              <tab.icon className="w-4 h-4" aria-hidden="true" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'calendar' && (
            <div className="animate-in fade-in duration-300">
              <CalendarView
                providerId={user.id}
                onBookingClick={handleBookingClick}
                onTimeSlotClick={handleTimeSlotClick}
              />
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="animate-in fade-in duration-300 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <BookingManager
                key={refreshKey}
                providerId={user.id}
                onMessageCustomer={handleMessageCustomer}
              />
            </div>
          )}

          {activeTab === 'today' && (
            <div className="animate-in fade-in duration-300">
              <TodaysFocus
                key={refreshKey}
                providerId={user.id}
                onBookingClick={handleBookingClick}
                onViewAll={() => setActiveTab('bookings')}
              />
            </div>
          )}

          {activeTab === 'manual' && (
            <div className="animate-in fade-in duration-300">
              <ManualBooking
                providerId={user.id}
                onBookingCreated={handleBookingCreated}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}